import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import { useRouter } from 'next/router'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { NativeModalBody, NativeModalFooter, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { stripEmoji } from '@Components/Personalization/Util/helpers'
import { AccordionImageCheckbox } from '@CoreComponents/Accordions/AccordionImageCheckbox'
import { Button } from '@CoreComponents/Buttons/Button'
import { CheckboxIndicator } from '@CoreComponents/Checkboxes/checkboxIndicator'
import { sanitizeText } from '@Domains/Cart/Components/Prompt/sanitize'
import { ProductCrossSellAndPersonalizationContentFragment } from '@Generated/graphql'
import { getPersonalization } from '@Globals/CMS/ProductHelpers'
import { useTranslation } from '@Globals/Translations'
import { TextInput } from '@Shared/Components/Inputs/TextInput/TextInput'

import styles from './EngravePrompt.module.scss'

export interface EngravePromptProps {
  availablePersonalizations: ProductCrossSellAndPersonalizationContentFragment[]
  initialValue?: string
  loading: boolean
  onSubmit: (productID: string | null, personalization?: { text: string }) => void
  selectedPersonalization: ProductCrossSellAndPersonalizationContentFragment | null
  useUpdateFlow?: boolean
}

const EngravingInfoList = [
  'products:NO_DELAY_ON_ENGRAVED_PRODUCTS',
  'products:SAME_FONT_AND_TEXT_SIZE_ADJUSTMENT_INFO',
  'products:ENGRAVED_PRODUCTS_ARE_NOT_RETURNABLE',
]

export const EngravePrompt: FC<EngravePromptProps> = ({
  availablePersonalizations,
  initialValue,
  loading,
  onSubmit,
  selectedPersonalization,
  useUpdateFlow,
}) => {
  const defaultPersonalization = useUpdateFlow && selectedPersonalization && initialValue ? getPersonalization(selectedPersonalization) : undefined

  const buttonRef = useRef<HTMLSpanElement | null>(null)
  const [value, setValue] = useState<Record<string, string>>({ [defaultPersonalization?.id]: initialValue || '' })
  const [selected, setSelected] = useState<any>(useUpdateFlow && defaultPersonalization?.id ? defaultPersonalization : undefined)

  const { locale = 'en-GB' } = useRouter()
  const { t } = useTranslation()
  const personalizations = useMemo(() => map(availablePersonalizations, getPersonalization), [availablePersonalizations])
  const disableButton = useMemo(() => {
    if (selected === undefined) {
      return true
    }
    if (useUpdateFlow) {
      return loading || !isNil(selected) ? (value[selected.id] || '').replaceAll(' ', '').length === 0 : false
    }
    return loading || isNil(selected) ? false : (value[selected.id] || '').replaceAll(' ', '').length === 0
  }, [loading, selected, useUpdateFlow, value])

  const selectedPrice = useMemo(() => {
    if (selected && !isEmpty(selected.discountedPrice)) {
      return `${selected?.discountedPrice ? `${selected?.discountedPrice} - ` : ''}`
    }
    return `${selected?.price ? `${selected?.price} - ` : ''}`
  }, [selected])

  useEffect(() => {
    ampli.promptOpen({ source_id: 'engraving_prompt' })
  }, [])

  return (
    <>
      <NativeModalHeader>
        <div className={styles.title}>{t('products:ENGRAVING_OPTIONS')}</div>
      </NativeModalHeader>
      <NativeModalBody>
        <div className={styles.engravePrompt}>
          <div className={styles.content}>
            {
                map(
                  personalizations,
                  personalization => (
                    <AccordionImageCheckbox
                      key={`EngravePromptCard_${personalization.id}`}
                      defaultChecked={useUpdateFlow && selected?.id === personalization.id}
                      header={(
                        <div className={styles.label}>
                          <CheckboxIndicator checked={selected?.id === personalization.id} round style={{ marginRight: '12px' }} />
                          {personalization.placement[locale]}
                        </div>
                            )}
                      id={personalization.id}
                      imgSrc={personalization.image}
                      name="engraving"
                      onClick={() => {
                        setSelected(personalization)
                        buttonRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          inline: 'nearest',
                          block: 'nearest',
                        })
                      }}
                    >
                      {!isEmpty(selected) && (
                        <div className={styles.inputWrapper}>
                          <p className={styles.inputLabel}>{t('products:ADD_YOUR_TEXT_HERE')}</p>
                          <TextInput
                            label={t('TEXT_MAX_X_CHARACTERS')
                              .replace('<span>X</span>', `${personalization?.maxCharacters || 'X'}`)}
                            onChange={(val) => {
                              let newValue = sanitizeText(val, personalization!.maxCharacters || 0)
                              newValue = stripEmoji(newValue)
                              setValue(prev => ({ ...prev, [personalization?.id]: newValue }))
                            }}
                            onKeyUp={(e) => {
                              if (e.key === 'Enter') {
                                onSubmit(personalization?.id || null, personalization ? { text: value[personalization.id] } : undefined)
                              }
                            }}
                            validations={[
                              v => ({
                                success: v.replaceAll(' ', '').length !== 0,
                                errorMessage: t('cms:INPUT_FIELD_IS_REQUIRED'),
                              }),
                            ]}
                            value={value[personalization.id] || ''}
                          />
                        </div>
                      )}

                      <ul className={styles.disclaimer}>
                        {map(EngravingInfoList, (item, i) => <li key={`EngravingInfoList_${i}`}>{t(item)}</li>)}
                      </ul>
                    </AccordionImageCheckbox>
                  ),
                )
              }
            <AccordionImageCheckbox
              defaultChecked={selected === null}
              header={(
                <div className={clsx(styles.label, styles.deSelect)}>
                  <CheckboxIndicator checked={selected === null} round style={{ marginRight: '12px' }} />
                  {t('cart:NO_ENGRAVING')}
                </div>
                  )}
              id="noEngravingCheckbox"
              name="engraving"
              onClick={() => {
                setSelected(null)
                buttonRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  inline: 'nearest',
                  block: 'nearest',
                })
              }}
            />
          </div>

        </div>
      </NativeModalBody>
      <NativeModalFooter className={clsx(styles.footer)}>
        <Button
          disabled={disableButton}
          fillWidth
          innerSpacing="medium"
          loading={loading}
          onClick={() => onSubmit(selected?.id || null, selected ? { text: value[selected.id] } : undefined)}
          theme="black"
        >
          {selectedPrice}
          {' '}
          {t(useUpdateFlow ? 'cms:UPDATE_LABEL' : 'cms:ADD_TO_CART')}
        </Button>

        <span ref={buttonRef} />
      </NativeModalFooter>
    </>
  )
}

